import {
  useQuery,
  UseQueryOptions,
  QueryKey,
  useMutation,
  UseMutationOptions,
} from 'react-query'
import { AxiosError, AxiosResponse } from 'axios'
import api from 'api'
import {
  UserDTO,
  UserSearchByCriteriaDTO,
  UserInformationDTO,
  ResetPasswordDTO,
} from './types'
import { User } from 'types'

const SERVICE_NAME = `User`

const QueryKeys = {
  current: `${SERVICE_NAME}#current`,
  single: `${SERVICE_NAME}#single`,
  all: `${SERVICE_NAME}#all`,
  count: `${SERVICE_NAME}#count`,
} as const

const MutationKeys = {
  create: `${SERVICE_NAME}#create`,
} as const

const CONTROLLER_PATH = `/app/spourgiti/admin/users`

const TIME = 1000 * 60 * 1 // 1 min

// POST create
export const createUser = async (payload: UserDTO): Promise<User> => {
  const { data }: AxiosResponse<User> = await api({
    method: 'post',
    url: `${CONTROLLER_PATH}`,
    data: {
      ...payload,
    },
  })
  return data
}

export const useCreateUser = (
  options: Omit<
    UseMutationOptions<User, AxiosError, UserDTO>,
    'mutationKey' | 'mutationFn'
  > = {}
) => {
  return useMutation(MutationKeys.create, createUser, {
    ...options,
  } as Omit<UseMutationOptions<User, AxiosError, UserDTO>, 'mutationKey' | 'mutationFn'>)
}

export const getCurrentUser = async (): Promise<UserInformationDTO> => {
  const { data } = await api({
    method: 'get',
    url: `${CONTROLLER_PATH}/me`,
  })
  return data
}

export const useCurrentUser = <T = UserInformationDTO>(
  options: UseQueryOptions<T, AxiosError, UserInformationDTO> = {}
) => {
  return useQuery<UserInformationDTO>(
    [QueryKeys.current],
    () => getCurrentUser(),
    {
      staleTime: Infinity,
      cacheTime: Infinity,
      ...options,
    } as Omit<
      UseQueryOptions<
        UserInformationDTO,
        AxiosError,
        UserInformationDTO,
        QueryKey
      >,
      'queryKey' | 'queryFn'
    >
  )
}

// Count
export const getCountUsers = async (
  filterOptions: UserSearchByCriteriaDTO
): Promise<number> => {
  const { data } = await api({
    method: 'post',
    url: `${CONTROLLER_PATH}/count-by-criteria`,
    data: filterOptions,
  })
  return data
}

export const useCountUsers = (
  filterOptions: UserSearchByCriteriaDTO = {} as UserSearchByCriteriaDTO,
  options: UseQueryOptions<number, AxiosError, number> = {}
) => {
  return useQuery<number>(
    [QueryKeys.count, filterOptions],
    () => getCountUsers(filterOptions),
    {
      staleTime: TIME,
      cacheTime: TIME,
      ...options,
    }
  )
}

// Search
export const searchUsersByCriteria = async (
  page,
  pageSize,
  filterOptions,
  sortOptions
): Promise<User[]> => {
  const { data } = await api({
    method: 'post',
    url: `${CONTROLLER_PATH}/search-by-criteria`,
    data: {
      pageNumber: page,
      pageSize,
      ...(filterOptions as object),
      ...(sortOptions as object),
    },
  })

  return data
}
export const useUsers = (
  page: number,
  pageSize: number,
  filters: UserSearchByCriteriaDTO = {},
  sortOptions: UserSearchByCriteriaDTO = {},
  options: UseQueryOptions<User[], AxiosError, User[]> = {}
) => {
  return useQuery<User[]>(
    [QueryKeys.all, page, pageSize, filters, sortOptions],
    () => searchUsersByCriteria(page, pageSize, filters, sortOptions),
    {
      staleTime: TIME,
      cacheTime: TIME,
      ...options,
    } as Omit<
      UseQueryOptions<User[], AxiosError, User[], QueryKey>,
      'queryKey' | 'queryFn'
    >
  )
}

// Update User
export const updateUser = async ({
  id,
  payload,
}: {
  id: number
  payload: UserInformationDTO
}) => {
  const { data }: AxiosResponse<User> = await api({
    method: 'put',
    url: `${CONTROLLER_PATH}/${id}`,
    data: {
      ...payload,
    },
  })
  return data
}

export const useUpdateUser = (
  options: Omit<
    UseMutationOptions<
      User,
      AxiosError,
      { id: number; payload: UserInformationDTO }
    >,
    'mutationKey' | 'mutationFn'
  > = {}
) => {
  return useMutation('updateUserMutation', updateUser, {
    ...options,
  } as Omit<UseMutationOptions<User, AxiosError, { id: number; payload: UserInformationDTO }>, 'mutationKey' | 'mutationFn'>)
}

// Request reset User Password
export const requestResetPassword = async ({
  email,
  token,
}: {
  email: string
  token: string
}) => {
  const { data } = await api({
    method: 'get',
    url: `${CONTROLLER_PATH}/request-reset-password/${email}?token=${token}`,
  })
  return data
}

export const useRequestResetPassword = (
  options: Omit<
    UseMutationOptions<User, AxiosError, { email: string; token: string }>,
    'mutationKey' | 'mutationFn'
  > = {}
) => {
  return useMutation('updateUserMutation', requestResetPassword, {
    ...options,
  })
}

// Request reset User Password
export const resetPassword = async (payload: ResetPasswordDTO) => {
  const { data } = await api({
    method: 'post',
    url: `${CONTROLLER_PATH}/reset-password`,
    data: {
      ...payload,
    },
  })
  return data
}

export const useResetPassword = (
  options: Omit<
    UseMutationOptions<User, AxiosError, ResetPasswordDTO>,
    'mutationKey' | 'mutationFn'
  > = {}
) => {
  return useMutation('resetPasswordMutation', resetPassword, {
    ...options,
  })
}

// GET user by id
export const getUserById = async (id): Promise<User> => {
  const { data } = await api({
    method: 'get',
    url: `${CONTROLLER_PATH}/${id}`,
  })
  return data
}

export const useUserById = <T = User>(
  id: number,
  options: UseQueryOptions<T, AxiosError, User> = {}
) => {
  return useQuery<User>(['user', id], () => getUserById(id), {
    ...options,
  } as Omit<UseQueryOptions<User, AxiosError, User, QueryKey>, 'queryKey' | 'queryFn'>)
}

const UserService = {
  searchUsersByCriteria,
  getUserById,
  resetPassword,
  requestResetPassword,
  updateUser,
  getCountUsers,
  getCurrentUser,
  createUser,
  hooks: {
    useRequestResetPassword,
    useResetPassword,
    useUserById,
    useUpdateUser,
    useUsers,
    useCountUsers,
    useCurrentUser,
    useCreateUser,
  },
  keys: { QueryKeys, MutationKeys },
}

export default UserService
